import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import CollapsibleSection from '../Layout/SectorDetail/component/CollapsibleSection';
import { INTERVALS } from '../Layout/SectorDetail/types';
import { SectorSchema, SensorModelSchema } from '../../../requests/api/apiTypes';

type TimeInterval = '1-day' | '1-week' | '2-weeks'
| '1-month' | '3-months' | '6-months' | null;

type ViewMode = 'clima' | 'todos';

interface StationInfoSidebarProps {
  station: SensorModelSchema;
  fedSectors: SectorSchema[];
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  selectedInterval: string | null;
  tempStartDate: string;
  tempEndDate: string;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onIntervalSelect: (interval: TimeInterval) => void;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onApplyDates: () => void;
  onAddStation: () => void;
  onDeleteStation: () => void;
}

const StationInfoSidebar: React.FC<StationInfoSidebarProps> = ({
  station,
  fedSectors,
  collapsed,
  setCollapsed,
  selectedInterval,
  tempStartDate,
  tempEndDate,
  viewMode,
  onViewModeChange,
  onIntervalSelect,
  onStartDateChange,
  onEndDateChange,
  onApplyDates,
  onAddStation,
  onDeleteStation,
}) => {
  const today = dayjs().format('YYYY-MM-DD');
  const sensorName = station.sensors?.[0]?.name || station.name;
  const lastActivity = station.lastActivity
    ? dayjs(station.lastActivity).format('DD/MM/YYYY HH:mm')
    : 'S/I';

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 64,
        right: 0,
        width: collapsed ? '50px' : '350px',
        minWidth: collapsed ? '50px' : '350px',
        height: 'calc(100vh - 64px)',
        transition: 'width 0.2s, min-width 0.2s',
        background: collapsed ? 'transparent' : '#f5f6fa',
        borderLeft: collapsed ? 'none' : '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
      }}
    >
      <IconButton
        onClick={() => setCollapsed(!collapsed)}
        size="small"
        sx={{
          position: 'absolute',
          top: 65,
          left: collapsed ? 8 : -16,
          background: '#fff',
          border: '1px solid #e0e0e0',
          boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
          zIndex: 1001,
          width: 32,
          height: 32,
          transition: 'left 0.2s',
          '&:hover': { background: '#f5f6fa' },
        }}
      >
        {collapsed ? <ChevronLeft /> : <ChevronRight />}
      </IconButton>
      {!collapsed && (
        <Box sx={{
          flex: 1,
          maxHeight: '100%',
          width: '100%',
          p: 3,
          pt: 0,
          paddingTop: '20px',
          overflowY: 'auto',
          overflowX: 'hidden',
          backgroundColor: 'white',
        }}>
          <Box display="flex" flexDirection="column" gap={0.5}>
            <CollapsibleSection
              title="Filtros de visualización"
              defaultExpanded={true}
              items={[]}
              renderBeforeItems={(
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: 'text.secondary', mb: 0 }}
                    >
                      Visualización
                    </Typography>
                    <TextField
                      value={viewMode}
                      select
                      onChange={(e) => onViewModeChange(
                        e.target.value as ViewMode,
                      )}
                      variant="standard"
                      fullWidth
                    >
                      <MenuItem value="clima">Clima</MenuItem>
                      <MenuItem value="todos">Todos</MenuItem>
                    </TextField>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: 'text.secondary', mb: 0 }}
                    >
                      Intervalos de
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {INTERVALS.map((interval) => (
                        <Chip
                          key={interval.key}
                          color={
                            selectedInterval === interval.key
                              ? 'primary' : undefined
                          }
                          label={interval.label}
                          onClick={() => onIntervalSelect(
                            interval.key as TimeInterval,
                          )}
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={0.5}
                    justifyContent="space-between"
                  >
                    <Box width="50%">
                      <Typography
                        variant="subtitle2"
                        sx={{ color: 'text.secondary', mb: 0 }}
                      >
                        Fecha de inicio
                      </Typography>
                      <TextField
                        type="date"
                        value={tempStartDate}
                        onChange={(e) => onStartDateChange(e.target.value)}
                        variant="standard"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: tempEndDate || today }}
                      />
                    </Box>
                    <Box width="50%">
                      <Typography
                        variant="subtitle2"
                        sx={{ color: 'text.secondary', mb: 0 }}
                      >
                        Fecha de fin
                      </Typography>
                      <TextField
                        type="date"
                        value={tempEndDate}
                        onChange={(e) => onEndDateChange(e.target.value)}
                        variant="standard"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: tempStartDate || undefined, max: today }}
                      />
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onApplyDates}
                    disabled={Boolean(
                      tempStartDate && tempEndDate
                        && tempStartDate > tempEndDate,
                    )}
                    fullWidth
                    sx={{ mt: 0.25 }}
                    size="small"
                  >
                    APLICAR
                  </Button>
                </Box>
              )}
            />
            <Divider />
            <CollapsibleSection
              title="Información estación"
              defaultExpanded={true}
              items={[
                { label: 'Nombre:', value: sensorName },
                {
                  label: 'Tipo sensor:',
                  value: station.sensorType || 'S/I',
                },
                { label: 'Zone ID:', value: station.zoneId || 'S/I' },
                { label: 'Estado:', value: station.status || 'S/I' },
                { label: 'Última actividad:', value: lastActivity },
              ]}
            />
            <Divider sx={{ my: 0.5 }} />
            <CollapsibleSection
              title="Sectores alimentados"
              defaultExpanded={true}
              items={[]}
              renderBeforeItems={
                fedSectors.length > 0 ? (
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {fedSectors.map((sector) => (
                      <Chip
                        key={sector.id}
                        label={sector.name}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay sectores usando esta estación
                  </Typography>
                )
              }
            />
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            gap={1}
            mt={3}
          >
            <Button
              variant="contained"
              fullWidth
              onClick={onAddStation}
              color="primary"
            >
              Agregar Estación de Clima
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={onDeleteStation}
              color="error"
            >
              Eliminar Estación de Clima
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default StationInfoSidebar;
